import { signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { WidgetState } from './widget.state';
import { inject } from '@angular/core';
import { IndicatorStore } from '../indicator/indicator.state';
import { DataLayerStore } from '../data-layer/data-layer.state';
import { ComparisonValueStore } from '../comparison-value/comparison-value.state';
import { WidgetConfig } from '../../services/grid-widget-config.service';
import { DataGroupProperty } from '../indicatpr-data/indicator-data.widget-selectors';
import { DataLayer } from '../../models/data-layer';
import { ComparisonValue } from '../../models/geo-data';

/**
 * Feature providing metadata-related methods for charts, such as label generation,
 * key retrieval, and grouping logic.
 */
export function withChartMetadataMethods() {
  return signalStoreFeature(
    {
      state: type<WidgetState>(),
    },
    withMethods(
      (
        store,
        indicatorStore = inject(IndicatorStore),
        layerStore = inject(DataLayerStore),
        comparisonStore = inject(ComparisonValueStore)
      ) => {
        /**
         * Processes and retrieves an array of labels or numerical values based on the provided group property.
         */
        const getLabelsByGroupProperty = (
          config: WidgetConfig,
          dataLayerIds: string[],
          dataKeys: string[]
        ): string[] | number[] => {
          if (!config) return [];

          switch (config.groupProperty) {
            case 'indicator':
              return indicatorStore
                .getByIds(config.indicators)
                .map(indicator =>
                  config.showFullIndicatorName
                    ? indicatorStore.getFullName(indicator)
                    : indicator.name
                );
            case 'feature':
              // eslint-disable-next-line no-case-declarations
              const features: any = {
                ...config.features,
                ...comparisonStore.byKey(),
              };
              return dataKeys.map(key => features[key].name);
            default: // dataLayer
              return layerStore
                .getByIds(dataLayerIds)
                .map((layer: DataLayer) => Number(layer.year))
                .filter(year => !config.disabledYears.includes(year))
                .sort((a, b) => b - a);
          }
        };

        /**
         * Retrieves an array of keys based on the group property defined in the configuration.
         */
        const getKeysByGroupProperty = (
          config: WidgetConfig,
          dataLayerIds: string[],
          dataKeys: string[]
        ): string[] => {
          if (!config) return [];
          switch (config.groupProperty) {
            case 'indicator':
              return indicatorStore
                .getByIds(config.indicators)
                .map(indicator => indicator._id);
            case 'feature':
              // eslint-disable-next-line no-case-declarations
              const features: any = {
                ...config.features,
                ...comparisonStore.byKey(),
              };
              return dataKeys.map(key => features[key].key);
            default: // dataLayer
              return layerStore
                .getByIds(dataLayerIds)
                .filter(
                  layer => !config.disabledYears.includes(Number(layer.year))
                )
                .sort((a, b) => b.year - a.year)
                .map(d => d._id);
          }
        };

        /**
         * Generates a dataset label based on the provided widget configuration, property type, and key.
         */
        const getDatasetLabel = (
          widget: WidgetConfig,
          property: string,
          key: string,
          indicatorPrefix = false,
          comparisonValues: Record<string, ComparisonValue>
        ): string => {
          if (!widget) return '';

          switch (property) {
            case 'indicator':
              if (indicatorPrefix) {
                let prefix = '';
                const indicator = indicatorStore.getById(key);
                if (!indicator) return '';
                if (indicator.parent) {
                  const parent = indicatorStore.getById(indicator.parent);
                  if (parent) {
                    prefix = parent.name;
                  }
                }
                return prefix ? `${prefix}: ${indicator.name}` : indicator.name;
              } else {
                return indicatorStore.getById(key)?.name || '';
              }
            case 'feature':
              return (
                widget.features?.[key]?.name ||
                comparisonValues[key]?.name ||
                ''
              );
            default:
              return String(layerStore.getById(key)?.year || '');
          }
        };

        /**
         * Retrieves the group name associated with a specific widget property and key.
         */
        const getGroup = (
          widget: WidgetConfig,
          property: string,
          key: string
        ): string | null => {
          if (!widget) return null;

          switch (property) {
            case 'indicator':
              return indicatorStore.getParent(key)?.name || null;
            default:
              return null;
          }
        };

        /**
         * Retrieves legend groups based on a widget's configuration.
         */
        const getLegendGroups = (
          widget: WidgetConfig
        ): Record<string, string[]> | null => {
          if (!widget) return null;

          if (widget.groupProperty === 'indicator') {
            const sets: Record<string, string[]> = {};
            widget.indicators.forEach((id: string) => {
              const parentName = indicatorStore.getParent(id)?.name;
              if (!parentName) return;
              if (!sets[parentName]) {
                sets[parentName] = [];
              }
              sets[parentName].push(indicatorStore.getById(id).name);
            });
            return sets;
          }
          return null;
        };

        /**
         * Retrieves the first suitable property from a predefined set that is neither focusProperty nor groupProperty.
         */
        const dataSetProperty = (conf: WidgetConfig): DataGroupProperty => {
          const possibleProps: DataGroupProperty[] = [
            'feature',
            'indicator',
            'dataLayer',
          ];
          return possibleProps.find(
            prop => ![conf.focusProperty, conf.groupProperty].includes(prop)
          )!;
        };

        /**
         * Gets valid keys by filtering out disabled items based on the dataset property.
         */
        const getValidKeys = (
          config: WidgetConfig,
          data: Record<string, any>,
          dsProperty: DataGroupProperty,
          dataLayerIds: string[]
        ): string[] => {
          const keys = Object.keys(data);

          if (!config) return keys;

          switch (dsProperty) {
            case 'feature':
              // eslint-disable-next-line no-case-declarations
              const disabledFeatures = config.disabledFeatures || [];
              // eslint-disable-next-line no-case-declarations
              const availableFeatureKeys = config.features
                ? Object.keys(config.features)
                : [];

              return keys.filter(
                key =>
                  !disabledFeatures.includes(key) &&
                  (availableFeatureKeys.length === 0 ||
                    availableFeatureKeys.includes(key))
              );

            case 'dataLayer':
              // eslint-disable-next-line no-case-declarations
              const disabledYears = config.disabledYears || [];

              return dataLayerIds.filter(layerId => {
                const layer = layerStore.getById(layerId);
                return layer && !disabledYears.includes(Number(layer.year));
              });

            case 'indicator':
              return keys;

            default:
              return keys;
          }
        };

        return {
          getLabelsByGroupProperty,
          getKeysByGroupProperty,
          getDatasetLabel,
          getGroup,
          getLegendGroups,
          dataSetProperty,
          getValidKeys,
        };
      }
    )
  );
}
